import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuCircleCheck, LuFileText, LuLoaderPinwheel, LuPaperclip, LuSend, LuTrash2, LuX } from 'react-icons/lu';

import { getAgentDisplayName } from '../../entities/chat-agent';
import { PROVIDER_AGENT, ProviderId } from '../../entities/provider';
import { WizardProposal } from '../../entities/wizard-proposal';
import { createSession } from '../../shared/api/chat-sessions';
import { auth } from '../../shared/firebase/firebase';
import { DashboardChatMessage, useDashboardChat } from '../../shared/hooks/use-dashboard-chat';
import { useWizardProposal } from '../../shared/hooks/use-wizard-proposal';
import { resolveApiProjectId, WIZARD_PROJECT_ID } from '../../shared/utils/virtual-projects';
import MarkdownRenderer from '../MarkdownRenderer';
import { AgentSwitcher } from './AgentSwitcher';
import ChatMessageActions from './ChatMessageActions';
import WizardProposalPanel from './WizardProposalPanel';

interface WizardPanelProps {
  /** When the platform is already chosen upstream (e.g. onboarding's platform-first step). */
  presetPlatform?: ProviderId;
  /** Fires once, the first time the build completes. */
  onBuilt?: (proposal: WizardProposal) => void;
  className?: string;
}

// The chat + upload + proposal-review UI for the "describe a spec, mFlow builds it" flow.
// Mounted from two places: OnboardingFlow's `wizard` step (first-run) and WizardLauncher
// (sidebar CTA, any time) — same session-creation/chat/proposal hooks, no duplicated logic.
const WizardPanel: React.FC<WizardPanelProps> = ({ presetPlatform, onBuilt, className }) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [docId, setDocId] = useState('');
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [manualAgent, setManualAgent] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const builtNotifiedRef = useRef(false);

  const { liveMessages: messages, typingText, sendMessage, uploadFile } = useDashboardChat();
  // This panel is always remounted fresh per launch (own effect below creates a brand-new
  // session), so `hasHistory: false` — a `built` snapshot restored on mount belongs to a past
  // wizard attempt, not this one; surface it as `priorBuilt` instead of the live panel.
  const { proposal, priorBuilt, dismissPriorBuilt, taskProgress, retry: retryBuild } = useWizardProposal(true, false);

  // Once built, WizardAgent's job (propose + build) is done — default to the platform agent that
  // actually knows how to keep managing the real project, but respect an explicit user override.
  const builtPlatform = proposal?.status === 'built' ? proposal.platform : undefined;
  const effectiveAgent = manualAgent ?? (builtPlatform ? PROVIDER_AGENT[builtPlatform] : 'WizardAgent');

  useEffect(() => {
    const userId = auth.currentUser?.uid ?? '';
    createSession(resolveApiProjectId(WIZARD_PROJECT_ID, userId), 'Wizard')
      .then((session) => setSessionId(session._id))
      .catch(() => setConnectError('Failed to start the assistant. Please try again.'));
    // Intentionally run once on mount — this panel is always remounted fresh per launch.
  }, []);

  useEffect(() => {
    if (proposal?.status === 'built' && !builtNotifiedRef.current) {
      builtNotifiedRef.current = true;
      onBuilt?.(proposal);
    }
  }, [proposal?.status]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, typingText]);

  const handleSend = (text = input) => {
    if (!text.trim() || !sessionId) return;
    sendMessage(text.trim(), {
      sessionId,
      // Once switched off WizardAgent, the platform agent needs the real built project/board id —
      // the wizard session itself is never project-scoped, so there's no other way it'd know.
      projectId: effectiveAgent !== 'WizardAgent' ? proposal?.builtProjectId : undefined,
      connectedAgents: [effectiveAgent],
      docId: docId || undefined,
    });
    setInput('');
    setFile(null);
    setDocId('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile || !sessionId) return;

    setFile(selectedFile);
    setIsFileLoading(true);
    uploadFile(
      selectedFile,
      auth.currentUser?.uid || '',
      sessionId,
      (uploadedDocId) => {
        toast.success('File processed successfully');
        setDocId(uploadedDocId);
        setIsFileLoading(false);
      },
      (error) => {
        toast.error(error);
        setFile(null);
        setIsFileLoading(false);
      },
    );
  };

  const handleRetryBuild = async () => {
    setIsRetrying(true);
    try {
      await retryBuild();
    } catch {
      toast.error('Failed to start retry.');
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      {connectError && (
        <div className="alert alert-error text-sm py-2">
          <span>{connectError}</span>
        </div>
      )}

      {priorBuilt && (
        <div className="rounded-xl bg-base-200 shadow-card px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 text-sm">
            <LuCircleCheck className="text-success shrink-0" />
            <span className="truncate">
              Your last wizard project — <b>{priorBuilt.projectName || 'Untitled project'}</b> on{' '}
              <span className="capitalize">{priorBuilt.platform}</span> — is already built. Describe a new spec below to
              start another.
            </span>
          </div>
          <button className="btn btn-ghost btn-xs btn-circle shrink-0" onClick={dismissPriorBuilt} title="Dismiss">
            <LuX size={14} />
          </button>
        </div>
      )}

      {/* Chat column grows to fill the space; the proposal — once one exists — gets its own
          fixed-width column on the right instead of stacking below the chat. */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {builtPlatform && (
            <AgentSwitcher builtPlatform={builtPlatform} activeAgent={effectiveAgent} onChange={setManualAgent} />
          )}

          <div className="rounded-xl bg-base-200 shadow-card flex-1 min-h-[280px] max-h-[55vh] overflow-y-auto p-4 space-y-3">
            {messages?.length === 0 && (
              <div className="text-sm opacity-40 text-center py-6">Describe your project below to get started.</div>
            )}
            {messages.map((msg: DashboardChatMessage, i) =>
              msg.isUser ? (
                <div key={i} className="flex justify-end">
                  <div className="rounded-xl rounded-br-sm bg-primary/15 text-sm px-4 py-2 max-w-[85%] whitespace-pre-wrap">
                    {msg.text}
                  </div>
                </div>
              ) : !msg.hidden ? (
                <div key={i} className="text-sm opacity-90 max-w-[90%]">
                  <div className="text-xs font-semibold opacity-50 mb-1">{getAgentDisplayName(msg.sender)}</div>
                  <MarkdownRenderer markdown={msg.text} />
                  {!!msg.actions?.length && <ChatMessageActions actions={msg.actions} />}
                </div>
              ) : null,
            )}
            {typingText && (
              <div className="flex items-center gap-2 text-sm opacity-50">
                <LuLoaderPinwheel className="animate-spin shrink-0" size={14} />
                {typingText}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {(file || isFileLoading) && (
            <div className="inline-flex items-center gap-2 rounded-lg bg-base-200 px-3 py-2">
              {isFileLoading ? (
                <LuLoaderPinwheel className="animate-spin opacity-70" />
              ) : (
                <LuFileText className="opacity-70" />
              )}
              <span className="text-sm truncate max-w-[300px]">{file?.name}</span>
              {!isFileLoading && (
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => {
                    setFile(null);
                    setDocId('');
                  }}
                >
                  <LuTrash2 />
                </button>
              )}
            </div>
          )}

          <div className="flex items-end gap-2">
            <label
              className="btn btn-ghost btn-sm btn-circle"
              title="Attach a spec file (.pdf, .doc, .docx, .md, .txt)"
            >
              <input
                type="file"
                accept=".pdf,.doc,.docx,.md,.markdown,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <LuPaperclip className="text-lg" />
            </label>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.currentTarget.style.height = 'auto';
                e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Describe your spec, or reply to refine the proposal…"
              rows={1}
              className="textarea textarea-bordered flex-1 bg-base-100 resize-none overflow-hidden text-sm"
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleSend()}
              disabled={!input.trim() || !sessionId}
            >
              <LuSend />
            </button>
          </div>
        </div>

        {proposal && (
          <div className="lg:w-[360px] shrink-0">
            <WizardProposalPanel
              proposal={proposal}
              taskProgress={taskProgress}
              onQuickAction={handleSend}
              onRetry={handleRetryBuild}
              isRetrying={isRetrying}
              presetPlatform={presetPlatform}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WizardPanel;
